import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { map, catchError, switchMap, tap } from 'rxjs/operators';
import { FeedbackActions } from './feedback.actions';
import { UserSettingsActions } from '../user-settings/user-settings.actions';
import { FeedbackApiService } from '@app/core/api/feedback';
import { LoggerService } from '@app/core/infrastructure';
import { ToastNotificationService } from '@app/core/infrastructure';

@Injectable()
export class FeedbackEffects {
  private readonly actions$ = inject(Actions);
  private readonly feedbackApiService = inject(FeedbackApiService);
  private readonly logger = inject(LoggerService).scope('FeedbackEffects');
  private readonly toastNotificationService = inject(ToastNotificationService);
  private readonly translate = inject(TranslateService);
  
  submitFeedback$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeedbackActions.submitFeedback),
      switchMap(({ feedback }) =>
        this.feedbackApiService.submitFeedback(feedback).pipe(
          map(response => FeedbackActions.submitFeedbackSuccess({ feedback: response })),
          catchError(error => {
            this.logger.error('Submit feedback failed:', error);
            return of(FeedbackActions.submitFeedbackFailure({ error: error.message }));
          })
        )
      )
    )
  );

  submitFeedbackSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeedbackActions.submitFeedbackSuccess),
      tap(() => {
        this.logger.info('Feedback submitted successfully');
        this.toastNotificationService.showSuccess(this.translate.instant('toast.feedbackThankYou'));
      }),
      // Refresh user settings to sync the updated collect_feedback flag (set to false)
      map(() => UserSettingsActions.loadSettings())
    )
  );

  submitFeedbackFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeedbackActions.submitFeedbackFailure),
      tap(({ error }) => {
        this.toastNotificationService.showError(this.translate.instant('toast.feedbackFailed'));
      })
    ),
    { dispatch: false }
  );

  dismissFeedback$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeedbackActions.dismissFeedback),
      switchMap(() =>
        this.feedbackApiService.dismissFeedback().pipe(
          map(response => FeedbackActions.dismissFeedbackSuccess({ response })),
          catchError(error => {
            this.logger.error('Dismiss feedback failed:', error);
            return of(FeedbackActions.dismissFeedbackFailure({ error: error.message }));
          })
        )
      )
    )
  );

  dismissFeedbackSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeedbackActions.dismissFeedbackSuccess),
      tap(() => {
        this.logger.debug('Feedback dismissed successfully');
      }),
      // Refresh user settings to sync the updated collect_feedback flag (set to false)
      map(() => UserSettingsActions.loadSettings())
    )
  );

  loadFeedbackHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeedbackActions.loadFeedbackHistory),
      switchMap(({ skip, limit }) =>
        this.feedbackApiService.getMyFeedback(skip || 0, limit || 10).pipe(
          map(feedbackList => {
            const hasMore = feedbackList.length === (limit || 10);
            return FeedbackActions.loadFeedbackHistorySuccess({ feedbackList, hasMore });
          }),
          catchError(error => {
            this.logger.error('Load feedback history failed:', error);
            return of(FeedbackActions.loadFeedbackHistoryFailure({ error: error.message }));
          })
        )
      )
    )
  );

  // Log successful operations
  logSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        FeedbackActions.loadFeedbackHistorySuccess
      ),
      tap(action => this.logger.debug('Success:', action.type))
    ),
    { dispatch: false }
  );
}
